import { defineStore } from "pinia";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import mapValues from "lodash/mapValues";
import { useProjectsStore } from "@/store/modules/projects";
import { useInstallationsStore } from "@/store/modules/installations";

const {
  innerFetch,
  innerCreate,
  innerPartialUpdate,
  innerBulkCreate,
  innerBulkUpdate,
} = crudGenerator(db.models.Vle);

const EMPTIABLE_KEYS = new Set([
  "flow1",
  "flow2",
  "flow3",
  "flow4",
  "complementary",
  "concentration",
  "concentrationExpected",
  "expectaction",
]);

export const useVlesStore = defineStore("vles", {
  state: () => ({
    vleList: [],
    vleIsLoading: false,
    speedVle: { concentrationExpected: 0, concentration: 0 },
    flowVle: { concentrationExpected: 0, concentration: 0 },
  }),
  actions: {
    async createVle(form) {
      const vle = await innerCreate(form, useProjectsStore().isOfflineState);
      await useInstallationsStore().refreshLastUpdated();
      this.upsertVle(vle);
      return vle;
    },

    async bulkCreateVle(form) {
      const vles = await innerBulkCreate(
        form,
        useProjectsStore().isOfflineState
      );

      this.vleList = [...this.vleList, ...vles];
      await useInstallationsStore().refreshLastUpdated();

      return vles;
    },

    async updateVle(form) {
      const cleanForm = mapValues(form, (value, key) => {
        if (!EMPTIABLE_KEYS.has(key)) return value;
        return value === "" ? null : value;
      });
      const vle = await innerPartialUpdate(
        cleanForm,
        useProjectsStore().isOfflineState
      );
      this.upsertVle(vle);
      return vle;
    },

    async bulkUpdateVles(form) {
      const vles = await innerBulkUpdate(
        form,
        useProjectsStore().isOfflineState
      );

      for (const vle of vles) {
        const index = this.vleList.findIndex(({ uuid }) => vle.uuid === uuid);
        this.vleList[index] = vle;
      }
      this.vleList = [...this.vleList];
      await useInstallationsStore().refreshLastUpdated();

      return vles;
    },

    async fetchVles(projectUuid, { installationUuid } = {}) {
      return innerFetch(
        installationUuid
          ? { installation: installationUuid }
          : { project: projectUuid },
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.vleIsLoading = isLoading;
        }
      );
    },

    async filterVle({ sumsUuids, configurationLinesUuids }) {
      while (this.vleIsLoading) await new Promise((r) => setTimeout(r, 200));
      this.vleIsLoading = true;
      try {
        const vles = await db.vles
          .filter(
            ({ configurationLine, sum }) =>
              sumsUuids.includes(sum) ||
              configurationLinesUuids.includes(configurationLine)
          )
          .toArray();

        for (const vle of vles) {
          await vle.setRelationships();
          const parameter = await vle.getParameter();
          if (parameter?.name === "vitesse") {
            this.speedVle = vle;
          }
          if (parameter?.name === "Débit") {
            this.flowVle = vle;
          }
        }

        this.vleList = [...vles];
      } finally {
        this.vleIsLoading = false;
      }
    },

    async retrieveVleBySumUuid(sumUuid) {
      const vle = await db.vles.get({ sum: sumUuid });
      await vle.setRelationships();
      return vle;
    },

    updateSpeedVle(newData) {
      this.speedVle = { ...this.speedVle, ...newData };
    },

    updateFlowVle(newData) {
      this.flowVle = { ...this.flowVle, ...newData };
    },

    upsertVle(vle) {
      const index = this.vleList.findIndex(({ uuid }) => vle.uuid === uuid);

      if (index < 0) {
        this.vleList.push(vle);
      } else {
        this.vleList[index] = vle;
        this.vleList = [...this.vleList];
      }
    },
  },
});
